import React, {forwardRef} from 'react';

import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import styles from './state-button.component.module.styl';

import type {ComponentProps, ReactNode, Ref} from 'react';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

/**
 * Optional text labels displayed on either side of the toggle track.
 * Both values are aria-hidden — they are decorative only.
 */
type CaptionType = {
    /** Label shown to the right of the toggle when it is in the on/checked state. */
    on?: ReactNode,
    /** Label shown to the left of the toggle when it is in the off/unchecked state. */
    off?: ReactNode,
};

type NativeInputProps = Omit<ComponentProps<'input'>, 'ref'>;

export interface Props extends NativeInputProps {
    /**
     * Decorative text labels shown on each side of the toggle track. Both are
     * aria-hidden, so also provide an `aria-label` or associate a visible
     * `<label>` for screen-reader users.
     */
    captions?: CaptionType,
    /**
     * Additional CSS class names applied to the root `<label>` element.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use rootProps.className instead
     */
    mix?: string,
    /**
     * Native `<label>` props spread onto the root element. Use `rootProps.className`
     * to add classes instead of the deprecated `mix` prop.
     */
    rootProps?: ComponentProps<'label'>,
    /**
     * Forwarded ref to the underlying `<input type="checkbox">` element. Use
     * this to read or set the checked state imperatively.
     */
    ref?: Ref<HTMLInputElement>,
    /**
     * Ref to the outer `<label>` root element. Use when you need to measure or
     * focus the container rather than the input.
     */
    rootRef?: Ref<HTMLLabelElement>,
}

/**
 * Toggle switch (on/off) implemented as a styled `<input type="checkbox">`
 * with `role="switch"` inside a `<label>` wrapper.
 *
 * The `captions` prop accepts optional `on` and `off` labels that are rendered
 * on each side of the toggle track and hidden from assistive technologies
 * (`aria-hidden`). Pair the component with a visible text label or pass
 * `aria-label` / `aria-labelledby` for screen reader users.
 *
 * The forwarded ref points to the underlying `<input>` element. Use `rootRef`
 * to access the outer `<label>` element instead.
 *
 * ## Accessibility
 * Implements the WAI-ARIA switch role. Pair with a visible label element or
 * an `aria-label` so screen readers can announce the toggle's purpose.
 * See [WAI-ARIA Authoring Practices — Switch](https://www.w3.org/WAI/ARIA/apg/patterns/switch/).
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-switch-state-button--documentation}
 */
export const StateButton = forwardRef<HTMLInputElement, Props>((props: Props, ref) => {
    const {
        captions,
        onChange,
        mix,
        rootProps,
        rootRef,
        className,
        ...inputProps
    } = props;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(event);
    };

    const classNamesList = classNames('state-button', mix, rootProps?.className);

    return (
        <label ref={rootRef} {...rootProps} className={classNamesList}>
            <input
                {...inputProps}
                className={classNames('state-button__handler', className)}
                ref={ref}
                role="switch"
                type="checkbox"
                aria-checked={inputProps.checked ?? false}
                onChange={handleChange}
            />
            {captions?.off ? (
                <span className={classNames('state-button__caption state-button__caption_off')} aria-hidden="true">
                    {captions.off}
                </span>
            ) : null}
            <span className={classes['state-button__box']}/>
            {captions?.on ? (
                <span className={classNames('state-button__caption state-button__caption_on')} aria-hidden="true">
                    {captions.on}
                </span>
            ) : null}
        </label>
    );
});

StateButton.displayName = 'StateButton';
